import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { LocationPreferencesModal } from './LocationPreferencesModal'

import { getErrorList } from 'errors/utils/getErrorList'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { STORAGE } from 'shared/enums/SitePreferences'
import useCurrency from 'shared/hooks/useCurrency'
import { colors } from 'shared/lib'
import { getSecureCookieFlag } from 'shared/utils/getSecureCookieFlag'
import { ClientOnly } from '../ClientOnly'

/**
 * This is the stateful button that encapsulates all the necessary functionality
 * for the button and modal to work in the app.
 *
 * Use the exported StyledCurrencyButton to mock the button inside Storybook.
 */
const CurrencySelectButton = () => {
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false)

  const { countryName, currencyCode, availableCurrencies } = useShoppingRegionContext()
  const { getCurrenciesErrors } = useCurrency()
  const isNewFooterEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_NEW_FOOTER)

  const { locale } = useRouter()

  const sixMonthExpiration = new Date()
  sixMonthExpiration.setMonth(sixMonthExpiration.getMonth() + 6)

  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (getCurrenciesErrors) {
      const errors = getErrorList(getCurrenciesErrors)
      setErrorMessage(errors[0]?.message || '')
    }
  }, [getCurrenciesErrors])

  const openCurrencyModal = () => {
    const currencyCookie = Cookies.get(STORAGE.CURRENCYCODE)
    const selectedCurrencySymbolFromCode = availableCurrencies?.filter(
      (currency) => currency.isoCode === currencyCookie,
    )[0]
    sendTrackingEvent('LOCATION_PREFERENCES_VIEW', {
      location: 'footer',
      page: window?.location?.href,
      shopping_region: Cookies.get(STORAGE.COUNTRYNAME),
      currency: `${currencyCookie}${selectedCurrencySymbolFromCode?.symbol}`,
      language: '',
    })
    setIsCurrencyModalOpen(true)
  }
  const closeCurrencyModal = () => {
    setIsCurrencyModalOpen(false)
    setErrorMessage('')
  }

  // TODO: As more locales get added change this into a reusable function
  // with a locale map
  useEffect(() => {
    // Check the cookie sent from regionPreferenceMiddleware to ensure modal shows on first render
    // but also set sessionStorage so it wont show on additional renders
    // but will once the sessionStorage is cleared
    const sessionConfirmationNeeded = Cookies.get('locationConfirmationNeeded')
    const prevCountry = Cookies.get('prevCountry')
    const sessionConfirmed = sessionStorage.getItem('locationConfirmed')
    if (
      prevCountry !== 'JP' &&
      sessionConfirmationNeeded === 'true' &&
      sessionConfirmed === null &&
      (locale?.toLowerCase() === 'ja-jp' || locale?.toLowerCase() === 'en-jp')
    ) {
      openCurrencyModal()
      sessionStorage.setItem('locationConfirmed', 'true')
      const secure = getSecureCookieFlag()
      Cookies.set('locationConfirmationNeeded', 'false', { expires: sixMonthExpiration, secure })
      Cookies.set('prevCountry', 'JP', { secure })
    }
  }, [])
  // TODO END

  return (
    <>
      <ClientOnly>
        <StyledCurrencyButton
          aria-label="Select Currency"
          title="Select Currency"
          onClick={openCurrencyModal}
          data-testid="currencySelectButton"
          data-qa="currencySelectButton"
          $isNewFooter={isNewFooterEnabled}
        >
          {`${countryName} / ${currencyCode}`}
        </StyledCurrencyButton>
      </ClientOnly>
      <LocationPreferencesModal
        isOpen={isCurrencyModalOpen}
        analyticsLocation="footer"
        closeCurrencyModal={closeCurrencyModal}
        errorMessage={errorMessage}
        data-testid="currencyModal"
      />
    </>
  )
}

const newFooterStyles = css`
  align-items: center;
  background-color: ${colors.FC2_BLACK};
  border: 1px solid ${colors.FC2_DARK_GREY};
  border-radius: 100px;
  color: ${colors.FC2_WHITE};
  display: inline-flex;
  font-size: 14px;
  height: 32px;
  padding: 6px 16px;
  white-space: nowrap;
`

const legacyStyles = css`
  border: 1.5px solid ${colors.FC2_DARK_GREY};
  border-radius: 30px;
  padding: 10px 24px;
`

export const StyledCurrencyButton = styled.button<{ $isNewFooter?: boolean }>`
  ${({ $isNewFooter }) => ($isNewFooter ? newFooterStyles : legacyStyles)}
`

export default CurrencySelectButton
